import { useState } from 'react';
import { mockPlans, planFeatures, PricingPlan } from '../../../mocks/superAdmin';

const SA_ACCENT = '#6366F1';

const CATEGORY_LABELS: Record<string, string> = {
  core: 'Core Modules',
  advanced: 'Advanced Features',
  integrations: 'Integrations',
  support: 'Support & SLA',
};

const CATEGORY_ICONS: Record<string, string> = {
  core: 'ri-apps-2-line',
  advanced: 'ri-rocket-line',
  integrations: 'ri-plug-line',
  support: 'ri-customer-service-2-line',
};

export default function SAPricingPlans() {
  const [plans, setPlans] = useState<PricingPlan[]>(mockPlans);
  const [editPlan, setEditPlan] = useState<PricingPlan | null>(null);
  const [saved, setSaved] = useState(false);

  const categories = Array.from(new Set(planFeatures.map((f) => f.category)));

  const toggleFeature = (planId: string, featureId: string) => {
    setPlans((prev) => prev.map((p) => {
      if (p.id !== planId) return p;
      const has = p.features.includes(featureId);
      return { ...p, features: has ? p.features.filter((f) => f !== featureId) : [...p.features, featureId] };
    }));
  };

  const openEdit = (plan: PricingPlan) => setEditPlan({ ...plan });

  const saveEdit = () => {
    if (!editPlan) return;
    setPlans((prev) => prev.map((p) => p.id === editPlan.id ? editPlan : p));
    setSaved(true);
    setTimeout(() => { setSaved(false); setEditPlan(null); }, 1500);
  };

  return (
    <div className="space-y-6">
      {/* Plan Cards */}
      <div className="grid grid-cols-4 gap-4">
        {plans.map((plan) => (
          <div key={plan.id} className={`bg-white rounded-2xl border-2 p-5 relative ${plan.isPopular ? 'border-indigo-400' : 'border-slate-100'}`}>
            {plan.isPopular && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="text-xs px-3 py-1 rounded-full bg-indigo-600 text-white font-semibold">Most Popular</span>
              </div>
            )}
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${plan.color}18` }}>
                <i className="ri-price-tag-3-line text-sm" style={{ color: plan.color }} />
              </div>
              <p className="font-bold text-slate-800">{plan.name}</p>
            </div>
            {plan.isCustom ? (
              <p className="text-2xl font-bold text-slate-800 mb-1">Custom</p>
            ) : (
              <div className="mb-1">
                <span className="text-2xl font-bold text-slate-900">AED {plan.price.toLocaleString()}</span>
                <span className="text-xs text-slate-400">/mo</span>
              </div>
            )}
            <div className="space-y-1.5 mt-3 mb-4 text-xs text-slate-500">
              <div className="flex items-center gap-1.5">
                <i className="ri-team-line" />
                <span>{plan.maxUsers === -1 ? 'Unlimited users' : `Up to ${plan.maxUsers} users`}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <i className="ri-calendar-check-line" />
                <span>{plan.maxBookings === -1 ? 'Unlimited bookings' : `${plan.maxBookings} bookings/mo`}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <i className="ri-hard-drive-2-line" />
                <span>{plan.maxStorage === -1 ? 'Unlimited storage' : `${plan.maxStorage} GB storage`}</span>
              </div>
            </div>
            <div className="flex items-center justify-between pt-3 border-t border-slate-50">
              <span className="text-xs text-slate-400">{plan.features.length} features</span>
              <button onClick={() => openEdit(plan)} className="text-xs font-semibold cursor-pointer hover:underline" style={{ color: SA_ACCENT }}>
                Edit Plan
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Feature Matrix */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-50">
          <h3 className="font-bold text-slate-800 text-sm">Feature Matrix</h3>
          <p className="text-xs text-slate-400 mt-0.5">Toggle which features are included in each plan</p>
        </div>

        {/* Header row */}
        <div className="grid border-b border-slate-100" style={{ gridTemplateColumns: '1fr repeat(4, 120px)' }}>
          <div className="px-6 py-3 text-xs font-bold text-slate-400 uppercase tracking-wider">Feature</div>
          {plans.map((p) => (
            <div key={p.id} className="px-4 py-3 text-center">
              <span className="text-xs font-bold text-slate-700">{p.name}</span>
            </div>
          ))}
        </div>

        {categories.map((cat) => (
          <div key={cat}>
            {/* Category header */}
            <div className="px-6 py-2.5 bg-slate-50 flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${CATEGORY_ICONS[cat]} text-sm text-slate-400`} />
              </div>
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{CATEGORY_LABELS[cat]}</span>
            </div>

            {planFeatures.filter((f) => f.category === cat).map((feature) => (
              <div key={feature.id} className="grid border-b border-slate-50 last:border-0 hover:bg-slate-50/40 transition-colors" style={{ gridTemplateColumns: '1fr repeat(4, 120px)' }}>
                <div className="px-6 py-3">
                  <p className="text-sm font-medium text-slate-700">{feature.name}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{feature.description}</p>
                </div>
                {plans.map((plan) => {
                  const included = plan.features.includes(feature.id);
                  return (
                    <div key={plan.id} className="px-4 py-3 flex items-center justify-center">
                      <button
                        onClick={() => !plan.isCustom && toggleFeature(plan.id, feature.id)}
                        className={`w-6 h-6 flex items-center justify-center rounded-full transition-all ${plan.isCustom ? 'opacity-40' : 'cursor-pointer hover:scale-110'}`}
                        style={included ? { backgroundColor: plan.color } : { backgroundColor: '#F1F5F9' }}
                      >
                        {included ? (
                          <i className="ri-check-line text-white text-xs" />
                        ) : (
                          <i className="ri-subtract-line text-slate-400 text-xs" />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Edit Plan Modal */}
      {editPlan && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-slate-800">Edit {editPlan.name} Plan</h3>
              <button onClick={() => setEditPlan(null)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer">
                <i className="ri-close-line" />
              </button>
            </div>
            {saved ? (
              <div className="flex items-center gap-2 text-emerald-600 justify-center py-8">
                <i className="ri-checkbox-circle-line text-2xl" />
                <span className="font-semibold">Plan updated!</span>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Plan Name</label>
                    <input type="text" value={editPlan.name} onChange={(e) => setEditPlan({ ...editPlan, name: e.target.value })}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Monthly Price (AED)</label>
                    <input type="number" value={editPlan.price} onChange={(e) => setEditPlan({ ...editPlan, price: Number(e.target.value) })}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Max Users (-1 = unlimited)</label>
                    <input type="number" value={editPlan.maxUsers} onChange={(e) => setEditPlan({ ...editPlan, maxUsers: Number(e.target.value) })}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Max Bookings/mo (-1 = unlimited)</label>
                    <input type="number" value={editPlan.maxBookings} onChange={(e) => setEditPlan({ ...editPlan, maxBookings: Number(e.target.value) })}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Storage (GB, -1 = unlimited)</label>
                    <input type="number" value={editPlan.maxStorage} onChange={(e) => setEditPlan({ ...editPlan, maxStorage: Number(e.target.value) })}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Accent Color</label>
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 rounded-xl border border-slate-200 flex-shrink-0" style={{ backgroundColor: editPlan.color }} />
                      <input type="text" value={editPlan.color} onChange={(e) => setEditPlan({ ...editPlan, color: e.target.value })}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm font-mono focus:outline-none" />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 pt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={editPlan.isPopular} onChange={(e) => setEditPlan({ ...editPlan, isPopular: e.target.checked })} className="rounded" />
                    <span className="text-sm text-slate-700">Mark as Popular</span>
                  </label>
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={() => setEditPlan(null)} className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer">Cancel</button>
                  <button onClick={saveEdit} className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold cursor-pointer" style={{ backgroundColor: SA_ACCENT }}>
                    Save Changes
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}